import { createSlice, createAsyncThunk, createSelector } from "@reduxjs/toolkit";
import { roomService } from "../services/room.service";
 
export const fetchRooms = createAsyncThunk("room/fetchAll", async (_, thunkAPI) => {
  try {
    return await roomService.getRooms();
  } catch (err) {
    return thunkAPI.rejectWithValue("Failed to fetch rooms");
  }
});
 
export const fetchRoomById = createAsyncThunk("room/fetchById", async (id, thunkAPI) => {
  try {
    return await roomService.getRoomById(id);
  } catch (err) {
    return thunkAPI.rejectWithValue("Failed to fetch room");
  }
});
 
export const addRoom = createAsyncThunk("room/add", async (room, thunkAPI) => {
  try {
    return await roomService.createRoom(room);
  } catch (err) {
    return thunkAPI.rejectWithValue("Failed to create room");
  }
});
 
export const editRoom = createAsyncThunk("room/edit", async ({ id, room }, thunkAPI) => {
  try {
    return await roomService.updateRoom(id, room);
  } catch (err) {
    return thunkAPI.rejectWithValue("Failed to update room");
  }
});
 
export const removeRoom = createAsyncThunk("room/remove", async (id, thunkAPI) => {
  try {
    await roomService.hardDeleteRoom(id);
    return id;
  } catch (err) {
    return thunkAPI.rejectWithValue("Failed to delete room");
  }
});
 
// --- Waiter assignment thunks ---
export const fetchRoomWaiters = createAsyncThunk(
  "room/fetchWaiters",
  async (roomId, thunkAPI) => {
    try {
      const waiters = await roomService.getRoomWaiters(roomId);
      return { roomId, waiters };
    } catch (err) {
      return thunkAPI.rejectWithValue("Failed to fetch room waiters");
    }
  },
);
 
export const assignWaiter = createAsyncThunk(
  "room/assignWaiter",
  async ({ roomId, userId }, thunkAPI) => {
    try {
      await roomService.assignWaiterToRoom(roomId, userId);
      return { roomId, userId };
    } catch (err) {
      return thunkAPI.rejectWithValue("Failed to assign waiter");
    }
  },
);
 
export const removeWaiter = createAsyncThunk(
  "room/removeWaiter",
  async ({ roomId, userId }, thunkAPI) => {
    try {
      await roomService.removeWaiterFromRoom(roomId, userId);
      return { roomId, userId };
    } catch (err) {
      return thunkAPI.rejectWithValue("Failed to remove waiter");
    }
  },
);
 
const roomSlice = createSlice({
  name: "room",
  initialState: {
    rooms: [],
    room: null,
    waitersByRoomId: {},
    loading: false,
    waitersLoading: false,
    error: null,
    showModal: false,
    showDeleteModal: false,
  },
  reducers: {
    openModal: (state) => {
      state.showModal = true;
    },
    closeModal: (state) => {
      state.showModal = false;
    },
    openDeleteModal: (state) => {
      state.showDeleteModal = true;
    },
    closeDeleteModal: (state) => {
      state.showDeleteModal = false;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchRooms.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchRooms.fulfilled, (state, action) => {
        state.loading = false;
        state.rooms = action.payload;
      })
      .addCase(fetchRooms.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchRoomById.fulfilled, (state, action) => {
        state.room = action.payload;
      })
      .addCase(addRoom.pending, (state) => {
        state.loading = true;
      })
      .addCase(addRoom.fulfilled, (state, action) => {
        state.rooms.push(action.payload);
        state.loading = false;
        state.showModal = false;
      })
      .addCase(editRoom.pending, (state) => {
        state.loading = true;
      })
      .addCase(editRoom.fulfilled, (state, action) => {
        const editedRoom = action.meta.arg;
        state.rooms = state.rooms.map((room) => {
          if (room.roomId === editedRoom.id) return { ...editedRoom.room, roomId: editedRoom.id };
          return room;
        });
        state.loading = false;
        state.showModal = false;
      })
      .addCase(removeRoom.pending, (state) => {
        state.loading = true;
      })
      .addCase(removeRoom.fulfilled, (state, action) => {
        const deletedRoomId = action.meta.arg;
        state.rooms = state.rooms.filter((r) => r.roomId !== deletedRoomId);
        state.loading = false;
        state.showDeleteModal = false;
      })
      // --- Waiter assignment ---
      .addCase(fetchRoomWaiters.pending, (state) => {
        state.waitersLoading = true;
      })
      .addCase(fetchRoomWaiters.fulfilled, (state, action) => {
        const { roomId, waiters } = action.payload;
        state.waitersByRoomId[roomId] = waiters;
        state.waitersLoading = false;
      })
      .addCase(fetchRoomWaiters.rejected, (state, action) => {
        state.waitersLoading = false;
        state.error = action.payload;
      })
      .addCase(assignWaiter.pending, (state) => {
        state.waitersLoading = true;
      })
      .addCase(assignWaiter.fulfilled, (state, action) => {
        const { roomId, waiters } = action.payload;
        state.waitersByRoomId[roomId] = waiters;
        state.waitersLoading = false;
      })
      .addCase(assignWaiter.rejected, (state, action) => {
        state.waitersLoading = false;
        state.error = action.payload;
      })
      .addCase(removeWaiter.pending, (state) => {
        state.waitersLoading = true;
      })
      .addCase(removeWaiter.fulfilled, (state, action) => {
        const { roomId, waiters } = action.payload;
        state.waitersByRoomId[roomId] = waiters;
        state.waitersLoading = false;
      })
      .addCase(removeWaiter.rejected, (state, action) => {
        state.waitersLoading = false;
        state.error = action.payload;
      });
  },
});
 
export const {
  openModal: openRoomModal,
  closeModal: closeRoomModal,
  openDeleteModal: openRoomDeleteModal,
  closeDeleteModal: closeRoomDeleteModal,
} = roomSlice.actions;
 
export const selectRooms = (state) => state.room.rooms;
export const selectRoomById = (id) =>
  createSelector([selectRooms], (rooms) => rooms.find((r) => r.roomId === id));
 
export const selectWaitersForRoom = (roomId) =>
  createSelector(
    [(state) => state.room.waitersByRoomId],
    (waitersByRoomId) => waitersByRoomId[roomId] || [],
  );
 
export default roomSlice.reducer;
 